// Phase 24 M4 — OTP→password migration landing.
//
// Reads `?token=` from the URL, accepts password + confirm, POSTs
// `/auth/setup-password`. On success the returned tokens are stored and
// the user lands on /marketplace.

import { useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { setupPassword } from '../api/auth';
import { useAuthContext } from '../context/AuthContext';

function describeAxiosError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { reason?: string; error?: string } | undefined;
    return data?.reason ?? data?.error ?? err.message ?? fallback;
  }
  return fallback;
}

export default function SetupPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const navigate = useNavigate();
  const { setSession } = useAuthContext();

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (pending) return;
    setError(null);
    if (password !== confirm) {
      setError('Os contrasinais non coinciden.');
      return;
    }
    setPending(true);
    try {
      const tokens = await setupPassword({ token, password });
      setSession(tokens);
      navigate('/marketplace', { replace: true });
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido completar a configuración.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <main
      className="min-h-[60vh] flex items-center justify-center px-6 py-12"
      style={{ background: '#F7F7F7', color: '#222222' }}
    >
      <div
        className="max-w-md w-full p-8 rounded-2xl"
        style={{
          background: '#FFFFFF',
          border: '1px solid #DDDDDD',
          boxShadow: 'var(--shadow-card)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <h1 className="text-2xl font-semibold mb-2" style={{ color: '#222222' }}>
          Define o teu contrasinal
        </h1>
        <p className="text-sm mb-6" style={{ color: '#717171' }}>
          Migramos a un sistema de contrasinal. Configúrao para volver acceder.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium">Contrasinal novo</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md"
              style={{
                background: '#FFFFFF',
                border: '1px solid #DDDDDD',
                color: '#222222',
              }}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Confirma contrasinal</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md"
              style={{
                background: '#FFFFFF',
                border: '1px solid #DDDDDD',
                color: '#222222',
              }}
            />
          </label>
          {error ? (
            <div
              role="alert"
              className="p-3 rounded-md text-sm"
              style={{
                background: '#FEE2E2',
                color: '#B23A3A',
                border: '1px solid #B23A3A',
              }}
            >
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={pending}
            className="w-full py-2 rounded-md font-semibold disabled:opacity-60"
            style={{
              background: '#2D6E1A',
              color: '#FFFFFF',
            }}
          >
            {pending ? 'Procesando…' : 'Gardar contrasinal'}
          </button>
        </form>
      </div>
    </main>
  );
}
